/** Окна цен: «Добавить цену» (ручная/контрактная, из шапки — поиском товара, или из «Без цены») и
 *  «Исправить» (новая строка с причиной, старая — заменённая). Вводится себестоимость С НДС без склада
 *  (так хранится); рядом — себестоимость = она + ставка склада на «Действует с». */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  addPrice,
  correctPrice,
  getCogsFrom,
  type HumanSource,
  type PriceRow,
} from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { dateRu, money, normalizeGuid, parseMoney, SOURCE_STYLE, VAT_MULT, ZERO_GUID } from "./format";
import { ChangeProduct, ProductCard, ProductSearch, type Product1c } from "./ProductPick";
import { PurchaseLine } from "./purchase";
import { rateBlocks, useRateOn } from "./rate";
import { invalidatePrices, KEY } from "./queries";
import { ErrorBox, Field, Modal } from "./ui";

/** Предзаполнение из «Без цены»: товар 1С известен и не редактируется. */
export interface PricePreset {
  product: Product1c;
  /** `cogs_from` ответа `/missing`; null — дату вводит человек */
  valid_from: string | null;
}

const ERR = "text-xs text-red-600 dark:text-red-400";

/** Подсказки к источнику — по строке под каждым вариантом. */
const SOURCE_HINT: Record<HumanSource, string> = {
  contract: "купили готовым у стороннего производителя (цена договора/накладной)",
  manual: "закупки не было, цена — оценка (старые товары)",
};

function NetHint({ raw }: { raw: string }): React.ReactElement | null {
  const p = parseMoney(raw, { positive: true });
  if (!p.ok) return null;
  return <>без НДС ≈ {money(Math.round((p.num / VAT_MULT) * 100) / 100)}</>;
}

/** Подпись характеристики для записи: «без характеристики» (нулевой GUID) имени не имеет. */
function charNameOf(p: Product1c): string | null {
  return p.characteristic_ref === ZERO_GUID ? null : p.characteristic_name?.trim() || null;
}

export function AddPriceDialog({
  preset,
  onClose,
}: {
  /** нет — товар ищется по проданным (или ссылки 1С вводятся вручную) */
  preset: PricePreset | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Product1c | null>(preset?.product ?? null);
  const [manual, setManual] = useState(false);
  const [nom, setNom] = useState("");
  const [char, setChar] = useState("");
  const [article, setArticle] = useState("");
  const [name, setName] = useState("");
  const [validFrom, setValidFrom] = useState(preset?.valid_from ?? "");
  const [source, setSource] = useState<HumanSource | null>(null);
  const [gross, setGross] = useState("");
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  // из шапки (без preset) «Действует с» по умолчанию — начало себестоимости, как в окне из «Без цены»;
  // неизвестна — пусто. Руками тронутую дату не перезаписываем.
  const dateTouched = useRef(false);
  const cogsFromQ = useQuery({ queryKey: KEY.cogsFrom, queryFn: getCogsFrom, enabled: !preset });
  const cogsFromDefault = cogsFromQ.data ?? null;
  useEffect(() => {
    if (cogsFromDefault && !dateTouched.current) setValidFrom((cur) => cur || cogsFromDefault);
  }, [cogsFromDefault]);
  const rate = useRateOn(validFrom);

  // ручной ввод ссылок — только пока товар не выбран
  const useManual = picked === null && manual;
  const nomN = picked ? picked.nomenclature_ref : useManual ? normalizeGuid(nom) : null;
  const charN = picked ? picked.characteristic_ref : useManual ? normalizeGuid(char) : null;
  const price = parseMoney(gross, { positive: true });
  const errors = {
    product: picked || useManual ? null : "выберите товар или введите ссылки 1С вручную",
    nom: !useManual || nomN ? null : "GUID вида xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    char: !useManual || charN ? null : "GUID вида xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    validFrom: validFrom ? null : "укажите дату",
    source: source ? null : "выберите источник",
    price: price.ok ? null : price.error,
    rate: validFrom ? rateBlocks(rate) : null,
    reason: reason.trim() ? null : "причина обязательна",
  };
  const valid = Object.values(errors).every((x) => x === null);

  const m = useMutation({
    mutationFn: (body: Parameters<typeof addPrice>[0]) => addPrice(body),
    onSuccess: async (r) => {
      toast.success(`Цена ${money(r.price_gross)} с ${dateRu(r.valid_from)} добавлена`);
      await invalidatePrices(qc);
      onClose();
    },
  });
  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTried(true);
    if (!valid || !nomN || !charN || !source || !price.ok) return;
    m.mutate({
      nomenclature_ref: nomN,
      characteristic_ref: charN,
      // из 1С — как есть; вручную — что ввели (пусто — бэк возьмёт из прошлой цены)
      article: picked ? picked.article_1c : article.trim() || null,
      name: picked ? picked.name_1c : name.trim() || null,
      characteristic_name: picked ? charNameOf(picked) : null,
      valid_from: validFrom,
      price_gross: price.value,
      source,
      reason: reason.trim(),
    });
  };
  const err = (k: keyof typeof errors): string | null => (tried ? errors[k] : null);

  return (
    <Modal title="Добавить цену" onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-3" data-testid="add-price-form">
        {picked ? (
          <ProductCard
            product={picked}
            action={preset ? undefined : <ChangeProduct onClick={() => setPicked(null)} />}
          />
        ) : (
          <>
            {!manual && (
              <ProductSearch
                error={err("product")}
                onPick={(p, cogsFrom) => {
                  setPicked(p);
                  if (!validFrom) setValidFrom(cogsFrom);
                }}
              />
            )}
            <details
              className="rounded-md border px-3 py-2 text-sm"
              open={manual}
              onToggle={(e) => setManual(e.currentTarget.open)}
              data-testid="manual-refs"
            >
              <summary className="cursor-pointer select-none text-muted-foreground">
                ввести ссылки 1С вручную (товар ещё не продавался)
              </summary>
              {manual && (
                <div className="mt-2 space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Номенклатура 1С (GUID)" error={err("nom")}>
                      {(id) => <Input id={id} value={nom} onChange={(e) => setNom(e.target.value)} />}
                    </Field>
                    <Field label="Характеристика 1С (GUID)" error={err("char")}>
                      {(id) => <Input id={id} value={char} onChange={(e) => setChar(e.target.value)} />}
                    </Field>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Артикул" hint="не указан — возьмётся из прошлой цены">
                      {(id) => <Input id={id} value={article} onChange={(e) => setArticle(e.target.value)} />}
                    </Field>
                    <Field label="Название">
                      {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </Field>
                  </div>
                </div>
              )}
            </details>
          </>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Действует с" error={err("validFrom")}>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={validFrom}
                onChange={(e) => {
                  dateTouched.current = true;
                  setValidFrom(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label="Себестоимость производства с НДС, ₽ (без склада)" error={err("price")} hint={<NetHint raw={gross} />}>
            {(id) => <Input id={id} inputMode="decimal" value={gross} onChange={(e) => setGross(e.target.value)} />}
          </Field>
        </div>
        {validFrom && <PurchaseLine raw={gross} rate={rate} />}
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">Источник</legend>
          <div className="space-y-1 pt-1 text-sm">
            {(["contract", "manual"] as const).map((s) => (
              <label key={s} className="flex items-baseline gap-2">
                <input type="radio" name="source" checked={source === s} onChange={() => setSource(s)} />
                <span>
                  {SOURCE_STYLE[s].label}
                  <span className="text-xs text-muted-foreground"> — {SOURCE_HINT[s]}</span>
                </span>
              </label>
            ))}
          </div>
          {err("source") && <p className={ERR}>{err("source")}</p>}
        </fieldset>
        <Field label="Причина" error={err("reason")}>
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              placeholder="например: накладная №… от …; оценка по аналогу …"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        {m.isError && <ErrorBox error={m.error} what="Цена не добавлена" />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={m.isPending}>
            Добавить цену
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function CorrectDialog({ row, onClose }: { row: PriceRow; onClose: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const [gross, setGross] = useState("");
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const price = parseMoney(gross, { positive: true });
  const rate = useRateOn(row.valid_from);
  const rateErr = rateBlocks(rate);
  const m = useMutation({
    mutationFn: (body: { price_gross: string; reason: string }) => correctPrice(row.id, body),
    onSuccess: async (r) => {
      toast.success(`Цена исправлена: ${money(r.price_gross)} с ${dateRu(r.valid_from)}`);
      await invalidatePrices(qc);
      onClose();
    },
  });
  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTried(true);
    if (!price.ok || !reason.trim() || rateErr) return;
    m.mutate({ price_gross: price.value, reason: reason.trim() });
  };
  return (
    <Modal title="Исправить цену" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="correct-form">
        <div className="text-sm">
          <div className="font-medium">
            {row.article ?? "—"} · {row.name ?? "—"}
          </div>
          <div className="text-muted-foreground">
            сейчас: себестоимость {money(row.price_gross)} с НДС ({SOURCE_STYLE[row.source].label}), действует с{" "}
            {dateRu(row.valid_from)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Исправление — новая строка на ту же дату с причиной; текущая станет заменённой (видна в
            истории).
          </p>
        </div>
        <Field
          label="Новая себестоимость производства с НДС, ₽ (без склада)"
          error={tried && !price.ok ? price.error : null}
          hint={<NetHint raw={gross} />}
        >
          {(id) => <Input id={id} inputMode="decimal" value={gross} onChange={(e) => setGross(e.target.value)} />}
        </Field>
        <PurchaseLine raw={gross} rate={rate} />
        <Field label="Причина" error={tried && !reason.trim() ? "причина обязательна" : null}>
          {(id) => <Textarea id={id} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        {m.isError && <ErrorBox error={m.error} what="Исправление не сохранено" />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={m.isPending}>
            Исправить
          </Button>
        </div>
      </form>
    </Modal>
  );
}
